/**
 * Return-distance pricing tiers — the frontend mirror of `resolveReturnDistanceTier` in
 * flexio-backend/src/modules/helpers.ts. Keep the two in sync; the backend is the source of
 * truth for what gets billed, this only explains it in the UI.
 *
 * Bracket billing, not incremental: whichever band the total return distance lands in, the
 * ENTIRE distance bills at that band's per-mile rate.
 *
 *   0 < miles <= maxReturnDistanceShort                    -> "none": free, not billed
 *   maxReturnDistanceShort < miles <= maxReturnDistanceLong -> "short": whole distance x pricePerReturnDistanceShort
 *   miles > maxReturnDistanceLong                           -> "long":  whole distance x pricePerReturnDistanceLong
 *
 * maxReturnDistanceShort defaults to 0 (nothing free) when unset. maxReturnDistanceLong defaults
 * to unbounded when unset, so a short-only rate card just bills every mile above the free
 * threshold at the short rate. A flat per-mile rate with no tiers is modeled by setting both
 * thresholds to the same value and both rates to the flat rate, so every billable mile falls
 * into the Long band.
 *
 * Return TIME (pricePerReturnTime, $/hr) is billed on top, whatever the tier.
 */
export const METERS_PER_MILE = 1609.344;

export const RETURN_TIER = {
  NONE: "none",
  SHORT: "short",
  LONG: "long"
};

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const metersToMiles = (meters) => num(meters) / METERS_PER_MILE;

export const resolveReturnDistanceTier = (miles, rate = {}) => {
  const distance = num(miles);
  const maxShort = num(rate?.maxReturnDistanceShort);
  const hasMaxLong =
    rate?.maxReturnDistanceLong !== null && rate?.maxReturnDistanceLong !== undefined;
  const maxLong = hasMaxLong ? num(rate?.maxReturnDistanceLong) : Infinity;
  const base = {
    distance,
    maxReturnDistanceShort: maxShort,
    maxReturnDistanceLong: hasMaxLong ? maxLong : maxShort
  };
  if (distance <= 0 || distance <= maxShort) {
    return { ...base, tier: RETURN_TIER.NONE, rate: 0 };
  }
  if (distance <= maxLong) {
    return {
      ...base,
      tier: RETURN_TIER.SHORT,
      rate: num(rate?.pricePerReturnDistanceShort)
    };
  }
  return {
    ...base,
    tier: RETURN_TIER.LONG,
    rate: num(rate?.pricePerReturnDistanceLong)
  };
};

/** Human label for a tier, with its threshold. */
export const describeReturnTier = (tier, rate = {}) => {
  const maxShort = num(rate?.maxReturnDistanceShort);
  const hasMaxLong =
    rate?.maxReturnDistanceLong !== null && rate?.maxReturnDistanceLong !== undefined;
  const maxLong = hasMaxLong ? num(rate?.maxReturnDistanceLong) : null;
  switch (tier) {
    case RETURN_TIER.SHORT:
      return maxLong !== null
        ? `Short (${maxShort}–${maxLong} mi)`
        : `Short (over ${maxShort} mi)`;
    case RETURN_TIER.LONG:
      return `Long (over ${maxLong ?? maxShort} mi)`;
    default:
      return `Free (up to ${maxShort} mi)`;
  }
};

/**
 * The three bands a rate configuration produces, for display. This is bracket billing: the
 * distance travelled decides which single band's rate applies to the WHOLE trip, there is no
 * separately-billed "gap" band between Short and Long — anything at/below the free threshold is
 * simply unbilled.
 */
export const describeReturnTiers = (rate = {}) => {
  const maxShort = num(rate?.maxReturnDistanceShort);
  const hasMaxLong =
    rate?.maxReturnDistanceLong !== null && rate?.maxReturnDistanceLong !== undefined;
  const maxLong = hasMaxLong ? num(rate?.maxReturnDistanceLong) : null;
  const shortRate = num(rate?.pricePerReturnDistanceShort);
  const longRate = num(rate?.pricePerReturnDistanceLong);
  return {
    free: {
      tier: RETURN_TIER.NONE,
      label: `Free — up to ${maxShort} mi`,
      rate: 0,
      active: true
    },
    short: {
      tier: RETURN_TIER.SHORT,
      label:
        maxLong !== null
          ? `Short return — ${maxShort}–${maxLong} mi`
          : `Short return — over ${maxShort} mi`,
      rate: shortRate,
      active: shortRate > 0
    },
    long: {
      tier: RETURN_TIER.LONG,
      label: `Long return — over ${maxLong ?? maxShort} mi`,
      rate: longRate,
      active: hasMaxLong && longRate > 0
    },
    time: {
      rate: num(rate?.pricePerReturnTime),
      active: num(rate?.pricePerReturnTime) > 0
    }
  };
};

/**
 * What a return leg would cost under a rate card: distance tier + return time. Same math as
 * calculatePricing's priceReturnDistanceShort / Long / priceReturnTime, so the plan page can
 * show a "what if" (e.g. the failed-stops return) without a round trip.
 */
export const estimateReturnCharge = (miles, hours, rate = {}) => {
  const resolved = resolveReturnDistanceTier(miles, rate);
  const distanceCharge = Math.round(resolved.distance * resolved.rate * 100) / 100;
  const timeCharge = Math.round(num(hours) * num(rate?.pricePerReturnTime) * 100) / 100;
  return {
    ...resolved,
    distanceCharge,
    timeCharge,
    total: Math.round((distanceCharge + timeCharge) * 100) / 100
  };
};

export const formatMoney = (value) => {
  const symbol = (typeof window !== "undefined" && window.CURRENCY_SYMBOL) || "$";
  return `${symbol}${num(value).toFixed(2)}`;
};

export const formatMiles = (meters, decimals = 1) =>
  `${metersToMiles(meters).toFixed(decimals)} mi`;

export const formatMins = (mins) => {
  const total = Math.round(num(mins));
  if (total < 60) return `${total} min`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
};
